import axiosRetry from 'axios-retry';
import csrfToken from '../helpers/csrfToken';
import { captureException } from '@paybis/frontend-common-lib/src/plugins/sentry';
import axios from './network';

import TokenService from '@paybis/frontend-common-lib/src/services/token';
import { isTokenRefreshError } from '@paybis/frontend-common-lib/src/services/tokenRefreshError';
import { shouldRefreshAndReplay } from '@paybis/frontend-common-lib/src/services/sessionRecovery';
import { isStatusCritical } from '@paybis/frontend-common-lib/src/services/error';
import { getCurrentLocale } from '../helpers/helper';

const language = getCurrentLocale();

const instance = axios.create({
  useUserLocale: true,
  headers: {
    'Accept-Language': language,
  },
});

axiosRetry(instance, { retries: 3, shouldResetTimeout: true });

instance.interceptors.request.use(
  async (config) => {
    config.headers['Content-Type'] = 'application/json';

    config.timeout = config.timeout || 30000; // timeout request

    if (TokenService.hasToken()) {
      config.headers['Authorization'] = `Bearer ${TokenService.getToken()}`;
    } else if(TokenService.hasRefreshToken()){
        const jwt = await requestNewToken(config);
        config.headers['Authorization'] = `Bearer ${jwt}`;
    }

    const { method, url } = config;

    if (method !== 'get' && url.indexOf('/') === 0) {
      config.headers['X-CSRF-TOKEN'] = csrfToken;
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    if (config.useUserLocale) {
      config.headers['X-User-Locale'] = language;
    }

    return config;
  },
  (error) => { throw error; },
);

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config = {}, response = null } = error;
    if (response === undefined) {
      throw new Error('Request failed with non-defined response');
    }

    if (response === null) {
      throw new Error('Request failed with empty response');
    }

    const { headers = {}, status = null } = response;
    let redirectUrl = headers['x-redirect'] || null;
    if (redirectUrl !== null) {
      if (config.skipRedirectIntercept) {
        return { ...response, data: { redirectUrl } };
      }
      window.location.assign(redirectUrl);
      return new Promise(() => {});
    }

    if ([401, 403].includes(status) && shouldRefreshAndReplay(config, error)) {
      const jwt = await requestNewToken(config).catch((refreshError) => {
        if (!isTokenRefreshError(refreshError)) {
          throw refreshError;
        }
      });
      if (jwt) {
        config.headers['Authorization'] = `Bearer ${jwt}`;
        return instance.request(config);
      }
    }

    if (isStatusCritical(status)) {
      captureException({ error });
    }

    Object.assign(error, error.response);
    throw error;
  },
);

async function requestNewToken(config) {
    const { url: requestUrl = '' } = config;
    if (
        requestUrl.indexOf('/jwt/refresh') === -1 &&
        requestUrl.indexOf('/') !== 0
    ) {
      const { jwt } = await TokenService.requestNewToken(requestUrl);
      return jwt;
    }
}

export default instance;
